import {
  DocumentTypesInfiniteSelectField,
  InfiniteTable,
  InputDate,
} from '@/components/_common';
import DocumentTypeColumnFilter from '@/components/_common/DocumentType/DocumentTypeColumnFilter';

import { COLUMNS_WIDTH, getTableScrollX } from '@/utils/table';
import { Alert, Input } from 'antd';
import { ColumnsType } from 'antd/es/table/interface';
import moment from 'moment';
import React, { useState } from 'react';
import { FaExchangeAlt } from 'react-icons/fa';
import {
  DirectSaleFinancialFormValues,
  DirectSaleFinancialInstallmentList,
} from '../DirectSaleFinancialTabs';
import formatterPrice from '@/utils/number/formatterPrice';
import { PaymentCondition } from '@/core/domain/enterprise/PaymentCondition/PaymentCondition.types';
import toFixed from '@/utils/number/toFixed';
import { DocumentType } from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.types';
import { CompanySettingsInterface } from '@/core/domain/enterprise/Companies/Companies.types';
import { getInstallmentValues } from '../DirectSaleFinancialTabs/getInstallmentValues';
import { useDocumentTypeMutation } from '@/core/domain/enterprise/DocumentTypes/DocumentTypes.hooks';
import { FinancialType } from '@/components/_common/FinancialTypeGenerate';

type Props = {
  installments: DirectSaleFinancialInstallmentList[];
  setInstallments: (installments: DirectSaleFinancialInstallmentList[]) => void;
  paymentCondition?: PaymentCondition;
  isStone: boolean;
  formValues: DirectSaleFinancialFormValues;
  companySetting?: CompanySettingsInterface;
  documentType?: DocumentType;
};

const DirectSaleFinancialInstallments: React.FC<Props> = ({
  installments,
  setInstallments,
  paymentCondition,
  isStone,
  formValues,
  companySetting,
  documentType,
}) => {
  const [filterDropdownOpen, setFilterDropdownOpen] = useState(false);

  const installmentSize = installments.length;

  const hasMax12InstallmentAndLower10 =
    installmentSize <= 12 &&
    installments.some((item) => item.installmentValue < 0.1);

  const hasOver12InstallmentAndLower10 =
    installmentSize > 12 &&
    installments.some((item) => item.installmentValue < 10);

  function setFieldValue([field, index]: [string, number], value: unknown) {
    setInstallments(
      installments.map((item, i) =>
        i === index ? { ...item, [field]: value } : item,
      ),
    );
  }

  function handleAllDocumentType(
    documentTypeId: number,
    documentType: DocumentType,
  ) {
    setInstallments(
      installments.map((item, i, filledArray) => {
        const formIncreaseDecrease =
          ((formValues?.increaseAmount || 0) +
            (formValues?.decreaseAmount || 0) * -1) /
          filledArray.length;

        const {
          realValue,
          receivableValue,
          installmentValue,
          discountIncrease,
          tariff,
        } = getInstallmentValues({
          documentType,
          index: i,
          installmentQuantity: filledArray.length,
          valueTotal: formValues.valueTotal as number,
          financialType: isStone ? FinancialType.Stone : FinancialType.Default,
          isInstallmentFree: !!companySetting?.isInstallmentFree,
          paymentCondition,
          increaseDecrease: formIncreaseDecrease,
        });

        return {
          ...item,
          documentTypeId,
          discountIncrease: discountIncrease,
          rateValue: tariff,
          realValue: realValue,
          installmentValue: installmentValue,
          receivableValue: receivableValue,
        };
      }),
    );

    setFilterDropdownOpen(false);
  }

  const documentTypeMutate = useDocumentTypeMutation();

  async function handleDocumentType(documentTypeId: number, index: number) {
    const newInstallment = installments.map((item, i) =>
      index === i ? { ...item, documentTypeId } : item,
    );

    let documentType: DocumentType | undefined;

    const result = await Promise.all(
      newInstallment.map(async (curr) => {
        if (!curr.documentTypeId) return curr;

        if (documentType?.id !== curr.documentTypeId) {
          documentType = await documentTypeMutate.mutateAsync(
            curr.documentTypeId,
          );
        }

        const installmentSize = newInstallment.filter(
          (item) => item.documentTypeId === curr.documentTypeId,
        ).length;

        const rate = documentType?.rates?.find(
          ({ numberInstallments }) => numberInstallments === installmentSize,
        );

        const tariff = +(
          ((documentType?.fixedRate
            ? documentType.percentRate
            : rate?.percentRate || 0) /
            100) *
          curr.installmentValue
        ).toFixed(2);

        const formIncreaseDecrease =
          ((formValues?.increaseAmount || 0) +
            (formValues?.decreaseAmount || 0) * -1) /
          newInstallment.length;

        const discountIncrease = toFixed(
          (curr.installmentValue / 100) * (paymentCondition?.percentage || 0) +
            formIncreaseDecrease,
          2,
        );

        const realValue = curr.installmentValue + discountIncrease - tariff;

        const receivableValue = curr.installmentValue + discountIncrease;

        return {
          ...curr,
          rateValue: tariff,
          realValue: +realValue.toFixed(2),
          receivableValue: +receivableValue.toFixed(2),
          discountIncrease: discountIncrease,
        };
      }),
    );

    setInstallments(result);
  }

  const columns: ColumnsType<DirectSaleFinancialInstallmentList> = [
    {
      dataIndex: 'description',
      title: 'Descrição',
      width: COLUMNS_WIDTH.default,
      render: (value: string, _, i) => (
        <Input
          value={value}
          onChange={({ target }) =>
            setFieldValue(['description', i], target.value)
          }
        />
      ),
    },
    {
      dataIndex: 'documentNumber',
      title: 'Número do documento',
      width: COLUMNS_WIDTH.default,
      render: (value: string, _, i) => (
        <Input
          value={value}
          onChange={({ target }) =>
            setFieldValue(['documentNumber', i], target.value)
          }
        />
      ),
    },
    {
      dataIndex: 'documentTypeId',
      title: 'Tipo de documento',
      width: COLUMNS_WIDTH.default,
      filterDropdownOpen,
      onFilterDropdownOpenChange: setFilterDropdownOpen,
      filterDropdown: !isStone
        ? () => (
            <DocumentTypeColumnFilter
              onChange={handleAllDocumentType}
              queryFilter={{ isStoneDocument: isStone }}
            />
          )
        : undefined,
      filterIcon: !isStone ? <FaExchangeAlt size={14} /> : null,
      render: (value: string, _, i) =>
        !isStone ? (
          <DocumentTypesInfiniteSelectField
            value={value}
            onChange={(id) => handleDocumentType(id, i)}
            queryFilter={{
              isFinancial: true,
              isStoneDocument: isStone,
            }}
          />
        ) : (
          documentType?.description || '-'
        ),
    },
    {
      dataIndex: 'dueDate',
      title: 'Vencimento',
      width: 100,
      render: (value: string, _, i) =>
        isStone ? (
          moment(value).format('DD/MM/YYYY')
        ) : (
          <InputDate
            value={moment(value)}
            onChange={(targetValue) =>
              setFieldValue(['dueDate', i], targetValue)
            }
          />
        ),
    },
    {
      dataIndex: 'installmentValue',
      title: 'Valor da parcela',
      width: COLUMNS_WIDTH.price,
      render: (value: number) =>
        isStone && value < 0.1 && installmentSize <= 12 ? (
          <span style={{ color: 'red' }}>{formatterPrice(value)}</span>
        ) : isStone && value < 10 && installmentSize > 12 ? (
          <span style={{ color: 'red' }}>{formatterPrice(value)}</span>
        ) : (
          formatterPrice(value)
        ),
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'discountIncrease',
      title: 'Acres./Desc.',
      ellipsis: true,
      render: (value: number) => formatterPrice(value),
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'receivableValue',
      title: 'Valor a receber',
      ellipsis: true,
      render: (value: number) => formatterPrice(value),
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'rateValue',
      title: 'Tarifa',
      ellipsis: true,
      render: (value: number) => formatterPrice(value),
    },
    {
      width: COLUMNS_WIDTH.price,
      dataIndex: 'realValue',
      title: 'Valor líquido',
      ellipsis: true,
      render: (value: number) => formatterPrice(value),
    },
  ];

  return (
    <>
      {hasMax12InstallmentAndLower10 && isStone && (
        <Alert
          style={{ marginTop: 24, marginBottom: 24 }}
          type="warning"
          description={
            <>
              <strong>Existem parcelas com menos de R$0,10</strong>
              <div>
                Financeiro gerado com a Stone não permite criação de menos ou
                igual de 12 parcelas com valor menor de R$0,10.
              </div>
            </>
          }
        />
      )}

      {hasOver12InstallmentAndLower10 && isStone && (
        <Alert
          style={{ marginTop: 24, marginBottom: 24 }}
          type="warning"
          description={
            <>
              <strong>Existem parcelas com menos de R$10,00</strong>
              <div>
                Financeiro gerado com a Stone não permite criação de mais de 12
                parcelas com valor menor que R$10,00.
              </div>
            </>
          }
        />
      )}

      <InfiniteTable
        size="middle"
        loading={!!!installments}
        rowKey={(_row, index) => String(index)}
        dataSource={installments}
        columns={columns}
        scroll={{ x: getTableScrollX(columns) }}
      />
    </>
  );
};

export default DirectSaleFinancialInstallments;
